import { useState, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Chip,
  Button
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LinkIcon from '@mui/icons-material/Link';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  useScriptByIdQuery,
  useChildScriptsQuery,
  useDeleteScript,
  useDeleteScripts
} from '../hooks/useScripts';
import {
  BuilderTab,
  TestTab,
  HistoryTab,
  GapsReportTab,
  FeedbackTab
} from '../components/tabs';
import { useUserStore } from '../../../stores/userStore';
import AddTestPointsDialog, {
  type CreateItemEntry
} from '../components/AddTestPointsDialog';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import { useApiToast } from '@hooks/useApiToast';
import type { Script, CreateScriptRequest } from '../types';
import type { ItemScriptData } from '../types/steps';
import { DEFAULT_CATALOG } from '../data/defaultCatalog';

const ScriptProfilePage = () => {
  const { scriptId } = useParams<{ scriptId: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: masterScript, isLoading } = useScriptByIdQuery(scriptId ?? '');
  const {
    data: children = [],
    isLoading: childrenLoading,
    isSuccess: childrenLoaded
  } = useChildScriptsQuery(scriptId ?? '');
  const deleteScript = useDeleteScript();
  const deleteScripts = useDeleteScripts();
  const { showSuccess, showError } = useApiToast();

  const bulkCreate = useMutation<Script[], Error, CreateScriptRequest[]>({
    mutationFn: async dtos => {
      const results = await Promise.all(
        dtos.map(dto =>
          bffApiClient.post<Script>('/api/mqtt-scripts', dto).then(r => r.data)
        )
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['scripts', 'children', scriptId]
      });
      queryClient.invalidateQueries({
        queryKey: ['scripts', 'list']
      });
      queryClient.invalidateQueries({
        queryKey: ['scripts', 'masters']
      });
      showSuccess('scripts.createSuccess');
      setAddItemDialogOpen(false);
      setDuplicateSourceSteps(null);
    },
    onError: error => {
      showError('scripts.createError', error);
    }
  });

  const [activeTab, setActiveTab] = useState(0);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [duplicateSourceSteps, setDuplicateSourceSteps] =
    useState<ItemScriptData | null>(null);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );

  const handleAddItems = useCallback(() => {
    setAddItemDialogOpen(true);
  }, []);

  const handleCreateItems = useCallback(
    (entries: CreateItemEntry[]) => {
      const dtos: CreateScriptRequest[] = entries.map(entry => ({
        globalId: entry.name,
        scriptType: 'ITEM',
        scriptData: entry.scriptData,
        createdBy: 'system',
        parentScriptId: scriptId
      }));
      bulkCreate.mutate(dtos);
    },
    [bulkCreate, scriptId]
  );

  const handleDuplicateItem = useCallback((item: Script) => {
    if (!item.scriptData) return;
    try {
      const parsed = JSON.parse(item.scriptData) as ItemScriptData;
      setDuplicateSourceSteps(parsed);
      setAddItemDialogOpen(true);
    } catch {
      // noop
    }
  }, []);

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      deleteScript.mutate(itemId, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['scripts', 'children', scriptId]
          });
        }
      });
    },
    [deleteScript, scriptId, queryClient]
  );

  const handleRemoveItems = useCallback(
    (itemIds: string[]) => {
      deleteScripts.mutate(itemIds, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['scripts', 'children', scriptId]
          });
        }
      });
    },
    [deleteScripts, scriptId, queryClient]
  );

  const isMaster = masterScript?.scriptType === 'MASTER';
  const userName = useUserStore(s => s.userName);

  const disabledTestPointIds = useMemo(() => {
    const refToId = new Map<string, string>();
    for (const func of DEFAULT_CATALOG.functions) {
      for (const range of func.ranges) {
        for (const tp of range.testPoints) {
          refToId.set(`${func.id}:${tp.nominal}`, tp.id);
        }
      }
    }

    const ids: string[] = [];
    for (const child of children) {
      if (!child.scriptData) continue;
      try {
        const parsed = JSON.parse(child.scriptData) as ItemScriptData;
        if (parsed.testPointRef) {
          const ref = parsed.testPointRef;
          const catalogId = refToId.get(`${ref.functionId}:${ref.nominal}`);
          if (catalogId) ids.push(catalogId);
        }
      } catch {
        // skip
      }
    }
    return ids;
  }, [children]);

  if (isLoading || !masterScript) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const isWoLinked =
    masterScript.globalId.startsWith('WO-') ||
    masterScript.globalId.startsWith('WOD-');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          p: 3,
          pb: 0,
          minWidth: 0
        }}
      >
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1.5,
              pb: 1.5,
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {masterScript.globalId}
              </Typography>
              {isWoLinked && (
                <Chip
                  icon={<LinkIcon />}
                  size="small"
                  label={
                    masterScript.globalId.startsWith('WO-')
                      ? 'Work Order'
                      : 'Work Order Detail'
                  }
                  color={
                    masterScript.globalId.startsWith('WO-')
                      ? 'primary'
                      : 'default'
                  }
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              component={RouterLink}
              to="/settings/administration/station-devices?power=1"
              size="small"
              variant="outlined"
              startIcon={<PowerSettingsNewIcon />}
            >
              {t(
                'scripts.stationPowerSettings',
                'Station power settings'
              )}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {masterScript.createdBy}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
              <Typography variant="body2" color="text.secondary">
                {new Date(masterScript.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            {masterScript.updatedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                />
                <Typography variant="body2" color="text.secondary">
                  Updated{' '}
                  {new Date(masterScript.updatedAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccountTreeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {children.length} {children.length === 1 ? 'item' : 'items'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 }
          }}
        >
          <Tab label={t('scripts.builder', 'Builder')} />
          <Tab label={t('scripts.test', 'Test')} />
          <Tab label={t('scripts.history', 'History')} />
          {isMaster && (
            <Tab label={t('scripts.gapsReport', 'Gaps Report')} />
          )}
          {isMaster && (
            <Tab label={t('scripts.metrologistFeedback', 'Feedback')} />
          )}
        </Tabs>
      </Box>

      <Box sx={{ p: 3, minWidth: 0 }}>
        {activeTab === 0 && (
          <BuilderTab
            masterScript={masterScript}
            children={children}
            childrenLoading={childrenLoading}
            childrenLoaded={childrenLoaded}
            onAddItems={handleAddItems}
            onDuplicateItem={handleDuplicateItem}
            onRemoveItem={handleRemoveItem}
            onRemoveItems={handleRemoveItems}
          />
        )}
        {activeTab === 1 && (
          <TestTab
            scriptId={scriptId ?? ''}
            globalId={masterScript.globalId}
            masterScript={masterScript}
            items={children}
          />
        )}
        {activeTab === 2 && <HistoryTab scriptId={scriptId ?? ''} />}
        {activeTab === 3 && isMaster && (
          <GapsReportTab masterScript={masterScript} />
        )}
        {activeTab === 4 && isMaster && (
          <FeedbackTab
            masterScript={masterScript}
            children={children}
            currentUser={userName}
          />
        )}
      </Box>

      <AddTestPointsDialog
        open={addItemDialogOpen}
        onClose={() => {
          setAddItemDialogOpen(false);
          setDuplicateSourceSteps(null);
        }}
        onCreate={handleCreateItems}
        isPending={bulkCreate.isPending}
        siblings={children}
        masterScript={masterScript}
        disabledTestPointIds={disabledTestPointIds}
        duplicateSource={duplicateSourceSteps}
      />
    </Box>
  );
};

export default ScriptProfilePage;
